import React, { FC, useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";

export interface CommonLayoutV2Props {
  currentHref: string;
  children: React.ReactNode;
  PropertyID?: string | null;
  propertyType?: string | null;
  // New props to track actual completion
  stepCompletionStatus?: {
    [key: number]: {
      completed: boolean;
      fieldsCompleted?: number;
      totalFields?: number;
    };
  };
}

const CommonLayoutV2: FC<CommonLayoutV2Props> = ({
  children,
  PropertyID,
  propertyType,
  stepCompletionStatus = {},
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!PropertyID) {
      navigate("/add-listing-1");
    }
  }, [PropertyID, navigate]);

  const isSharedProperty = propertyType === "Shared Property";

  const pathArray = [
    { path: 1, type: "Description", href: "/add-listing-1", icon: "📝" },
    { path: 2, type: "Price", href: "/add-listing-2", icon: "💰" },
    { path: 3, type: "Images", href: "/add-listing-3", icon: "📸" },
    { path: 4, type: "Details", href: "/add-listing-4", icon: "🏠" },
    { path: 5, type: "Location", href: "/add-listing-5", icon: "📍" },
    { path: 6, type: "Amenities", href: "/add-listing-6", icon: "✨" },
    { path: 7, type: "Calendar", href: "/add-listing-7", icon: "📅", notApplicableForShared: true },
    { path: 8, type: "Rooms", href: "/add-listing-8", icon: "🛏️" },
    { path: 9, type: "Preview", href: "/add-listing-9", icon: "👁️" },
  ].map((step) => {
    if (PropertyID) {
      return {
        ...step,
        href: `${step.href}/${PropertyID}`,
      };
    }
    return step;
  });

  const activeStepIndex = pathArray.findIndex((step) =>
    location.pathname.startsWith(step.href.split("?")[0])
  );

  const activeStep = pathArray[activeStepIndex];

  // Check if a step is not applicable for shared properties
  const isStepNotApplicable = (step: any) => {
    return isSharedProperty && step.notApplicableForShared;
  };

  // Calculate actual completion percentage based on completed steps
  const calculateCompletionPercentage = () => {
    const applicableSteps = pathArray.filter(step => !isStepNotApplicable(step));
    const completedSteps = applicableSteps.filter(
      (step) => stepCompletionStatus[step.path]?.completed
    ).length;
    return Math.round((completedSteps / applicableSteps.length) * 100);
  };

  // Calculate progress bar width (shows visited steps)
  const calculateProgressBarWidth = () => {
    const applicableSteps = pathArray.filter(step => !isStepNotApplicable(step));
    const completedSteps = applicableSteps.filter(
      (step) => stepCompletionStatus[step.path]?.completed
    ).length;
    return (completedSteps / applicableSteps.length) * 100;
  };

  const completionPercentage = calculateCompletionPercentage();
  const progressBarWidth = calculateProgressBarWidth();
  const applicableSteps = pathArray.filter(step => !isStepNotApplicable(step));
  const completedStepsCount = applicableSteps.filter((step) => stepCompletionStatus[step.path]?.completed).length;

  return (
    <div
      className="nc-PageAddListing1 px-4 max-w-5xl mx-auto pb-20 pt-6 sm:pt-10 lg:pb-28"
      data-nc-id="PageAddListing1"
    >
      {/* Progress Stepper */}
      <div className="mb-8 sm:mb-12 mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-4 sm:p-6 lg:p-8">
          
          {/* Mobile Compact View */}
          <div className="block lg:hidden">
            {/* Current Step Header */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  stepCompletionStatus[activeStep?.path]?.completed
                    ? "bg-green-500"
                    : "bg-[#F56948]"
                }`}>
                  {stepCompletionStatus[activeStep?.path]?.completed ? (
                    <CheckIcon className="w-5 h-5 text-white" />
                  ) : (
                    <span className="text-lg">{activeStep?.icon}</span>
                  )}
                </div>
                <div className="text-left">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    Step {activeStep?.path} of {pathArray.length}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {activeStep?.type}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#F56948]">
                  {completionPercentage}%
                </span>
                {isMobileMenuOpen ? (
                  <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </button>

            {/* Progress Bar */}
            <div className="mt-4 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#F56948] transition-all duration-500 ease-out"
                style={{ width: `${progressBarWidth}%` }}
              ></div>
            </div>

            {/* Expandable Step List */}
            {isMobileMenuOpen && (
              <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
                {pathArray.map((step) => {
                  const stepStatus = stepCompletionStatus[step.path];
                  const isCompleted = stepStatus?.completed || false;
                  const isActive = step.path === activeStep?.path;
                  const notApplicable = isStepNotApplicable(step);

                  return (
                    <div
                      key={step.path}
                      className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                        notApplicable
                          ? "bg-gray-100 dark:bg-gray-800 opacity-60"
                          : isActive
                          ? "bg-[#F56948] bg-opacity-10 border-2 border-[#F56948]"
                          : "bg-gray-50 dark:bg-gray-900 border-2 border-transparent hover:border-gray-300 dark:hover:border-gray-700"
                      } ${notApplicable ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      onClick={(e) => {
                        if (notApplicable) {
                          e.preventDefault();
                        } else {
                          navigate(step.href);
                          setIsMobileMenuOpen(false);
                        }
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex items-center justify-center w-8 h-8 rounded-full ${
                            notApplicable
                              ? "bg-gray-300 dark:bg-gray-600"
                              : isCompleted
                              ? "bg-green-500"
                              : isActive
                              ? "bg-[#F56948]"
                              : "bg-gray-300 dark:bg-gray-700"
                          }`}
                        >
                          {notApplicable ? (
                            <span className="text-sm font-bold text-white">N/A</span>
                          ) : isCompleted ? (
                            <CheckIcon className="w-4 h-4 text-white" />
                          ) : (
                            <span className="text-sm font-bold text-white">
                              {step.path}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className={`text-sm font-medium ${
                            notApplicable
                              ? "text-gray-400 dark:text-gray-500 line-through"
                              : isActive
                              ? "text-[#F56948]"
                              : "text-gray-900 dark:text-white"
                          }`}>
                            {step.type}
                            {notApplicable && (
                              <span className="ml-2 text-xs">(Shared Property)</span>
                            )}
                          </div>
                          {!notApplicable && stepStatus?.fieldsCompleted !== undefined &&
                            stepStatus?.totalFields !== undefined &&
                            !isCompleted && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {stepStatus.fieldsCompleted}/{stepStatus.totalFields} fields
                              </div>
                            )}
                        </div>
                      </div>
                      {notApplicable ? (
                        <span className="text-xs font-semibold text-gray-400">
                          Skipped
                        </span>
                      ) : isCompleted ? (
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400">
                          Complete
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Mobile Stats */}
            <div className="mt-4 flex items-center justify-between text-xs">
              <span className="text-gray-600 dark:text-gray-400">
                {completedStepsCount} of {applicableSteps.length} completed
              </span>
              <span className={`font-semibold ${
                completionPercentage === 100
                  ? "text-green-600 dark:text-green-400"
                  : "text-[#F56948]"
              }`}>
                {completionPercentage === 100 ? "🎉 All Done!" : `${completionPercentage}%`}
              </span>
            </div>
          </div>

          {/* Desktop Horizontal Stepper */}
          <div className="hidden lg:block">
            {/* Progress Bar Background */}
            <div className="relative flex items-center justify-between mb-2">
              <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700 rounded-full">
                <div
                  className="h-full bg-[#F56948] rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: `${progressBarWidth}%`,
                  }}
                ></div>
              </div>

              {/* Step Indicators */}
              <div className="relative flex justify-between w-full">
                {pathArray.map((step, index) => {
                  const stepStatus = stepCompletionStatus[step.path];
                  const isCompleted = stepStatus?.completed || false;
                  const isActive = index === activeStepIndex;
                  const isFuture = !isCompleted && !isActive;
                  const notApplicable = isStepNotApplicable(step);

                  return (
                    <div
                      key={step.path}
                      className="flex flex-col items-center relative group"
                      style={{ width: `${100 / pathArray.length}%` }}
                    >
                      {/* Circle/Check Indicator */}
                      {notApplicable ? (
                        <div
                          className="relative flex items-center justify-center w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-600 opacity-60 z-10 cursor-not-allowed"
                        >
                          <span className="text-sm font-bold text-white">N/A</span>
                        </div>
                      ) : (
                        <Link
                          to={step.href}
                          className={`relative flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 z-10 ${
                            isCompleted
                              ? "bg-[#F56948] shadow-lg shadow-[#F56948]/30 scale-100"
                              : isActive
                              ? "bg-[#F56948] shadow-xl shadow-[#F56948]/40 scale-110 ring-4 ring-[#F56948]/20 dark:ring-[#F56948]/30"
                              : "bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 scale-90"
                          } group-hover:scale-110`}
                        >
                          {isCompleted ? (
                            <CheckIcon className="w-5 h-5 text-white" />
                          ) : (
                            <span
                              className={`text-sm font-bold ${
                                isActive
                                  ? "text-white"
                                  : isFuture
                                  ? "text-gray-500 dark:text-gray-400"
                                  : "text-white"
                              }`}
                            >
                              {step.path}
                            </span>
                          )}
                        </Link>
                      )}

                      {/* Step Label */}
                      {notApplicable ? (
                        <div className="text-xs sm:text-sm text-center mt-3 px-2 font-medium text-gray-400 dark:text-gray-500 line-through opacity-60 cursor-not-allowed">
                          {step.type}
                        </div>
                      ) : (
                        <Link
                          to={step.href}
                          className={`text-xs sm:text-sm text-center mt-3 px-2 font-medium transition-colors duration-200 ${
                            isActive
                              ? "text-[#F56948] dark:text-[#F56948] font-semibold"
                              : isCompleted
                              ? "text-[#F56948] dark:text-[#F56948] font-semibold"
                              : "text-gray-500 dark:text-gray-500"
                          } hover:text-[#F56948] dark:hover:text-[#F56948] whitespace-nowrap`}
                        >
                          {step.type}
                        </Link>
                      )}

                      {/* Field completion indicator (if available) */}
                      {!notApplicable && stepStatus?.fieldsCompleted !== undefined &&
                        stepStatus?.totalFields !== undefined &&
                        !isCompleted && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {stepStatus.fieldsCompleted}/{stepStatus.totalFields}
                          </div>
                        )}

                      {/* Tooltip on hover */}
                      <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                        <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-1 px-3 whitespace-nowrap shadow-lg">
                          {notApplicable
                            ? `N/A for Shared Property`
                            : isCompleted
                            ? `✓ ${step.type} Complete`
                            : `Step ${step.path}: ${step.type}`}
                          <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45"></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Progress Text */}
            <div className="mt-8 flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400 font-medium">
                {completionPercentage === 0
                  ? "Let's get started!"
                  : `${completedStepsCount} of ${applicableSteps.length} steps completed`}
              </span>
              <span
                className={`font-semibold ${
                  completionPercentage === 0
                    ? "text-gray-500 dark:text-gray-400"
                    : completionPercentage === 100
                    ? "text-green-600 dark:text-green-400"
                    : "text-[#F56948] dark:text-[#F56948]"
                }`}
              >
                {completionPercentage === 100 ? "🎉 " : ""}
                {completionPercentage}% Complete
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="listingSection__wrap">{children}</div>
    </div>
  );
};

export default CommonLayoutV2;